import { createStyles } from '@mantine/core';
import { memo } from 'react';
import useTranslation from 'next-translate/useTranslation';

import { CurrencyConvertor } from '~/components/utils/formatters';
import { Avatar } from '~/domains/design-system/Avatar';
import { Text } from '~/domains/design-system/Text';
import { Button } from '~/domains/design-system/Button';
import { Title } from '~/domains/design-system/Title';
import { Radio } from '~/domains/design-system/components/Radio/Radio';

import { formatHandle } from '../../utils/formatHandle';
import { bpsToPercent, organizerShareBps } from '../../utils/groupShareMath';
import { PickControlBadge } from '../PickControlBadge/PickControlBadge';

import { GroupUserRow } from './GroupUserRow';
import type { GroupContestInfo, GroupRecipientDraft } from './types';

import dt from '../../testing.const';

type PickControlSubtitleProps = {
  shareBps: number;
  isController: boolean;
  isLosingControl: boolean;
};

const useStyles = createStyles((theme) => ({
  content: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minHeight: 0,
  },
  scrollArea: {
    flex: '0 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    padding: `0 ${theme.other.spacing._24}px`,
  },
  heading: {
    margin: `${theme.other.spacing._16}px 0 0`,
  },
  subtitle: {
    display: 'block',
    color: theme.colors.gray2[3],
    marginBottom: theme.other.spacing._12,
  },
  summaryCard: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._12,
    padding: theme.other.spacing._16,
    background: theme.colors.gray2[0],
    borderRadius: theme.radius.md,
    marginBottom: theme.other.spacing._8,
  },
  summaryText: {
    display: 'flex',
    flexDirection: 'column',
    flexGrow: 1,
    minWidth: 0,
  },
  summaryContestName: {
    color: theme.colors.gray2[3],
  },
  summaryFee: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    flexShrink: 0,
  },
  summaryFeeLabel: {
    color: theme.colors.gray2[3],
  },
  statusLine: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.other.spacing._4,
  },
  percent: {
    color: theme.colors.gray2[3],
  },
  dot: {
    color: theme.colors.gray2[3],
  },
  loseWarning: {
    color: theme.colors.orange[5],
  },
  rowWithDivider: {
    borderBottom: `1px solid ${theme.colors.gray2[0]}`,
  },
  radio: {
    '& .mantine-Radio-radio:checked': {
      backgroundColor: theme.white,
      borderColor: theme.colors.blue[7],
    },

    '& .mantine-Radio-icon': {
      color: theme.colors.blue[7],
    },
  },
  footer: {
    padding: theme.other.spacing._16,
  },
}));

type PickControlStepProps = {
  entryName?: string;
  contest: GroupContestInfo;
  organizerHandle?: string;
  organizerAvatarUrl?: string;
  recipients: GroupRecipientDraft[];
  /** null = the organizer ("You") keeps pick control. */
  pickControllerUserId: string | null;
  onPickControllerChange: (userId: string | null) => void;
  onSubmit: () => void;
  isSubmitting: boolean;
  /** Terminal sent state: hosts dismiss on success but not instantly, so the CTA lands on a disabled "sent" label. */
  hasSucceeded: boolean;
};

function PickControlSubtitleComponent({
  shareBps,
  isController,
  isLosingControl,
}: PickControlSubtitleProps) {
  const { t } = useTranslation('group');
  const { classes } = useStyles();

  return (
    <span className={classes.statusLine}>
      <Text variant="body-medium" className={classes.percent}>
        {bpsToPercent(shareBps)}%
      </Text>
      {(isController || isLosingControl) && (
        <Text variant="body-medium" className={classes.dot}>
          •
        </Text>
      )}
      {isController && <PickControlBadge />}
      {isLosingControl && (
        <Text variant="body-medium" className={classes.loseWarning}>
          {t('panel.youWillLosePickControl')}
        </Text>
      )}
    </span>
  );
}

const PickControlSubtitle = memo(PickControlSubtitleComponent);

export function PickControlStep({
  entryName,
  contest,
  organizerHandle,
  organizerAvatarUrl,
  recipients,
  pickControllerUserId,
  onPickControllerChange,
  onSubmit,
  isSubmitting,
  hasSucceeded,
}: PickControlStepProps) {
  const { t } = useTranslation('group');
  const { classes } = useStyles();

  const organizerBps = organizerShareBps(recipients.map((recipient) => recipient.shareBps));
  const organizerKeepsControl = pickControllerUserId === null;

  return (
    <div className={classes.content}>
      <div className={classes.scrollArea}>
        <Title variant="title-x-large" className={classes.heading} tag="h3">
          {t('panel.setUpPickControl')}
        </Title>
        <Text variant="title-medium" className={classes.subtitle} component="p">
          {t('panel.setUpPickControlSubtitle')}
        </Text>

        <div className={classes.summaryCard}>
          <Avatar handle={organizerHandle ?? ''} src={organizerAvatarUrl} size={32} />
          <div className={classes.summaryText}>
            {entryName !== undefined && <Text variant="subhead-small">{entryName}</Text>}
            <Text variant="body-medium" className={classes.summaryContestName}>
              {contest.name}
            </Text>
          </div>
          <div className={classes.summaryFee}>
            <Text variant="subhead-small">{CurrencyConvertor(contest.entryFeeCents / 100)}</Text>
            <Text variant="body-medium" className={classes.summaryFeeLabel}>
              {t('panel.entryFee')}
            </Text>
          </div>
        </div>

        <div role="radiogroup" aria-label={t('panel.pickControl')}>
          <GroupUserRow
            handle={organizerHandle ?? ''}
            label={t('panel.you')}
            avatarUrl={organizerAvatarUrl}
            subtitle={
              <PickControlSubtitle
                shareBps={organizerBps}
                isController={organizerKeepsControl}
                isLosingControl={!organizerKeepsControl}
              />
            }
            className={classes.rowWithDivider}
            onClick={() => onPickControllerChange(null)}
            rightSection={
              <Radio
                aria-label={t('panel.organizerPickControl')}
                name="group-entry-pick-controller"
                className={classes.radio}
                checked={organizerKeepsControl}
                onChange={() => onPickControllerChange(null)}
                size="sm"
                data-test-id={dt.panel.pickControlRadio}
              />
            }
            data-test-id={dt.panel.pickControlRow}
          />

          {recipients.map((recipient) => {
            const isController = pickControllerUserId === recipient.userId;

            return (
              <GroupUserRow
                key={recipient.userId}
                handle={recipient.handle}
                avatarUrl={recipient.avatarUrl}
                subtitle={
                  <PickControlSubtitle
                    shareBps={recipient.shareBps}
                    isController={isController}
                    isLosingControl={false}
                  />
                }
                className={classes.rowWithDivider}
                onClick={() => onPickControllerChange(recipient.userId)}
                rightSection={
                  <Radio
                    aria-label={formatHandle(recipient.handle)}
                    name="group-entry-pick-controller"
                    className={classes.radio}
                    checked={isController}
                    onChange={() => onPickControllerChange(recipient.userId)}
                    size="sm"
                    data-test-id={dt.panel.pickControlRadio}
                  />
                }
                data-test-id={dt.panel.pickControlRow}
              />
            );
          })}
        </div>
      </div>

      <div className={classes.footer}>
        <Button
          fullWidth
          variant="primary-fill"
          size="large"
          loading={isSubmitting}
          disabled={hasSucceeded}
          onClick={onSubmit}
          data-test-id={dt.panel.submitButton}
          data-loading={isSubmitting || undefined}
        >
          {hasSucceeded ? t('panel.invitesSent') : t('panel.groupEntry')}
        </Button>
      </div>
    </div>
  );
}
